<script setup>
import { ref, h } from 'vue';
import { useI18n } from 'vue-i18n';
import { resolveDirective, withDirectives } from 'vue';
import {
  createColumnHelper,
  FlexRender,
  getCoreRowModel,
  getSortedRowModel,
  useVueTable,
} from '@tanstack/vue-table';

import Button from 'dashboard/components-next/button/Button.vue';
import RowRenderer from './RowRenderer.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  hideBillable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['goToConversation']);

const { t } = useI18n();
const columnHelper = createColumnHelper();
const sorting = ref([]);

const getSortIconClass = column => {
  if (column.getIsSorted() === 'asc') return 'i-lucide-chevron-up';
  if (column.getIsSorted() === 'desc') return 'i-lucide-chevron-down';
  return 'i-lucide-chevrons-up-down opacity-50';
};

const formatStatus = status => {
  const statusMap = {
    sent: {
      label: 'Enviado',
      wrapperClass:
        'inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap bg-n-amber-2 text-n-amber-11 border border-n-amber-7',
      iconClass: 'font-bold text-sm text-n-amber-10',
      icon: '✓',
    },
    delivered: {
      label: 'Não Lida',
      wrapperClass:
        'inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap bg-n-slate-2 text-n-slate-11 border border-n-slate-7',
      iconClass: 'font-bold text-sm text-n-slate-10',
      icon: '✓✓',
    },
    read: {
      label: 'Lida',
      wrapperClass:
        'inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap bg-n-blue-2 text-n-blue-11 border border-n-blue-7',
      iconClass: 'font-bold text-sm text-n-blue-9',
      icon: '✓✓',
    },
    failed: {
      label: 'Falhou',
      wrapperClass:
        'inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap bg-n-ruby-2 text-n-ruby-11 border border-n-ruby-7',
      iconClass: 'font-bold text-sm text-n-ruby-9',
      icon: '✗',
    },
    rejected: {
      label: 'Rejeitada',
      wrapperClass:
        'inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap bg-n-ruby-2 text-n-ruby-11 border border-n-ruby-7',
      iconClass: 'font-bold text-sm text-n-ruby-9',
      icon: '✗',
    },
    pending: {
      label: 'Pendente',
      wrapperClass:
        'inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap bg-n-iris-2 text-n-iris-11 border border-n-iris-7',
      iconClass: 'font-bold text-sm text-n-iris-9',
      icon: '⏳',
    },
    queued: {
      label: 'Na Fila',
      wrapperClass:
        'inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap bg-n-iris-2 text-n-iris-11 border border-n-iris-7',
      iconClass: 'font-bold text-sm text-n-iris-9',
      icon: '⏳',
    },
  };

  const info = statusMap[status] || {
    label: status,
    wrapperClass:
      'inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap bg-n-slate-3 text-n-slate-11 border border-n-slate-7',
    iconClass: 'font-bold text-sm',
    icon: '?',
  };

  return h('div', { class: info.wrapperClass }, [
    h('span', { class: info.iconClass }, info.icon),
    h('span', { class: 'text-xs' }, info.label),
  ]);
};

const getErrorTranslation = errorMessage => {
  if (!errorMessage) return 'Nenhum';

  const codeMatch = errorMessage.match(/^(\d+)/);
  if (codeMatch) {
    const errorCode = codeMatch[1];
    const translationKey = `CAMPAIGN.WHATSAPP_OFFICIAL.DELIVERIES.ERROR_CODES.${errorCode}`;
    const translation = t(translationKey);
    if (translation !== translationKey) {
      return `${errorCode} - ${translation}`;
    }
  }

  const lowerError = errorMessage.toLowerCase();
  if (lowerError.includes('undeliverable')) {
    return t('CAMPAIGN.WHATSAPP_OFFICIAL.DELIVERIES.ERROR_CODES.131026');
  }
  if (lowerError.includes('rate limit')) {
    return t('CAMPAIGN.WHATSAPP_OFFICIAL.DELIVERIES.ERROR_CODES.130429');
  }
  if (lowerError.includes('blocked')) {
    return t('CAMPAIGN.WHATSAPP_OFFICIAL.DELIVERIES.ERROR_CODES.80007');
  }

  return errorMessage;
};

const makeSortHeader =
  label =>
  ({ column }) =>
    h(
      'button',
      {
        class:
          'flex items-center gap-2 hover:text-n-slate-12 transition-colors',
        onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
      },
      [label, h('i', { class: getSortIconClass(column) })]
    );

const allColumns = [
  columnHelper.accessor('contact_name', {
    header: makeSortHeader('Contato'),
    cell: info => info.getValue() || 'N/A',
    enableSorting: true,
    enableGlobalFilter: true,
  }),

  columnHelper.accessor('phone_number', {
    header: makeSortHeader('Telefone'),
    cell: info => info.getValue(),
    enableSorting: true,
    enableGlobalFilter: true,
  }),

  columnHelper.accessor('status', {
    header: makeSortHeader('Status'),
    cell: info => formatStatus(info.getValue()),
    enableSorting: true,
    filterFn: (row, id, value) => {
      if (!value || (Array.isArray(value) && value.length === 0)) return true;
      const rowValue = row.getValue(id);
      return Array.isArray(value)
        ? value.includes(rowValue)
        : rowValue === value;
    },
  }),

  columnHelper.accessor('created_at', {
    header: makeSortHeader('Data/Hora'),
    cell: info => new Date(info.getValue()).toLocaleString('pt-BR'),
    enableSorting: true,
  }),

  columnHelper.accessor('billable', {
    header: makeSortHeader('Faturável'),
    cell: info => {
      const value = info.getValue();
      return h(
        'span',
        { class: value ? 'text-green-700 font-semibold' : 'text-n-slate-10' },
        value ? 'Sim' : 'Não'
      );
    },
    enableSorting: true,
    filterFn: (row, id, value) => {
      if (value === undefined || value === null) return true;
      return row.getValue(id) === value;
    },
  }),

  columnHelper.accessor('error_message', {
    header: makeSortHeader('Erro'),
    cell: info => {
      const error = info.getValue();
      if (!error) return 'Nenhum';
      const translatedError = getErrorTranslation(error);
      const displayText =
        translatedError.length > 30
          ? translatedError.substring(0, 30) + '...'
          : translatedError;
      const tooltip = resolveDirective('tooltip');
      return withDirectives(
        h(
          'span',
          {
            class:
              'text-xs py-1 px-2 rounded cursor-help inline-block max-w-full whitespace-nowrap overflow-hidden text-ellipsis',
          },
          displayText
        ),
        [[tooltip, translatedError, 'right']]
      );
    },
    enableSorting: true,
    enableGlobalFilter: true,
  }),

  {
    id: 'actions',
    header: () => 'Ações',
    enableSorting: false,
    cell: ({ row }) => {
      const tooltip = resolveDirective('tooltip');
      const displayId = row.original.display_id;
      const canNavigate = !!displayId;
      const tooltipText = displayId
        ? 'Ir para Conversa'
        : 'Conversa não disponível';

      return h('div', { class: 'flex gap-2 items-center justify-center' }, [
        withDirectives(
          h(Button, {
            variant: 'faded',
            color: canNavigate ? 'blue' : 'slate',
            size: 'xs',
            icon: 'i-lucide-message-square',
            disabled: !canNavigate,
            onClick: canNavigate
              ? () => emit('goToConversation', row)
              : undefined,
            class: canNavigate ? '' : 'opacity-50 cursor-not-allowed',
          }),
          [[tooltip, tooltipText, 'right']]
        ),
      ]);
    },
  },
];

const columns = props.hideBillable
  ? allColumns.filter(col => col.accessorKey !== 'billable')
  : allColumns;

const table = useVueTable({
  get data() {
    return props.data;
  },
  columns,
  state: {
    get sorting() {
      return sorting.value;
    },
  },
  manualPagination: true,
  manualFiltering: true,
  onSortingChange: updater => {
    sorting.value =
      typeof updater === 'function' ? updater(sorting.value) : updater;
  },
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
});

defineExpose({ table });
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div class="flex flex-col flex-1 min-h-0">
    <!-- Loading state (initial load only — caller controls visibility) -->
    <div
      v-if="isLoading && data.length === 0"
      class="flex items-center justify-center flex-1"
    >
      <div
        class="flex flex-col items-center justify-center p-12 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 max-w-md w-full mx-4"
      >
        <div class="relative flex items-center justify-center w-20 h-20 mb-6">
          <div
            class="absolute inset-0 border-4 border-green-200 dark:border-green-900 border-t-green-500 dark:border-t-green-400 rounded-full animate-spin"
          />
          <span class="i-lucide-radio text-green-500 absolute w-8 h-8" />
        </div>
        <h3
          class="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-2 text-center"
        >
          Carregando Lista de Transmissão
        </h3>
        <p class="text-sm text-slate-500 dark:text-slate-400 text-center mb-6">
          {{
            $t('CAMPAIGN.WHATSAPP_OFFICIAL.DELIVERIES.TABLE.LOADING_MESSAGE')
          }}
        </p>
        <div class="flex items-center gap-2">
          <span class="loading-dot loading-dot-1" />
          <span class="loading-dot loading-dot-2" />
          <span class="loading-dot loading-dot-3" />
        </div>
      </div>
    </div>

    <!-- Table -->
    <div
      v-else-if="data.length > 0"
      class="flex-1 min-h-0 overflow-auto rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800 shadow-sm"
    >
      <table class="w-full min-w-[1000px] border-collapse border-spacing-0">
        <thead class="sticky top-0 z-10 bg-green-200 dark:bg-n-slate-2">
          <tr
            v-for="headerGroup in table.getHeaderGroups()"
            :key="headerGroup.id"
          >
            <th
              v-for="header in headerGroup.headers"
              :key="header.id"
              class="font-semibold text-green-800 dark:text-green-300 p-2 text-left border-b border-green-100 dark:border-green-800 text-sm bg-green-200 dark:bg-green-900/20"
            >
              <FlexRender
                v-if="!header.isPlaceholder"
                :render="header.column.columnDef.header"
                :props="header.getContext()"
              />
            </th>
          </tr>
        </thead>
        <tbody class="bg-white dark:bg-slate-900/20">
          <RowRenderer
            :rows="table.getRowModel().rows"
            :depth="0"
            :table="table"
            :is-expanding="false"
          />
        </tbody>
      </table>
    </div>

    <!-- Empty filter state -->
    <div v-else class="flex items-center justify-center py-16">
      <div
        class="flex flex-col items-center justify-center p-8 bg-n-slate-2 dark:bg-n-slate-3 rounded-xl border border-n-slate-6 text-center max-w-md"
      >
        <span class="i-lucide-search-x size-12 text-n-slate-9 mb-3" />
        <h3 class="text-lg font-semibold text-n-slate-12 mb-1">
          Nenhum resultado encontrado
        </h3>
        <p class="text-sm text-n-slate-10 mb-4">
          Não há entregas com o filtro selecionado.
        </p>
        <p class="text-xs text-n-slate-9">
          Clique em "Total" ou "Limpar" para ver todas as entregas.
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Bouncing dots animation — kept as @keyframes is not expressible in Tailwind */
.loading-dot {
  @apply w-2 h-2 bg-green-500 rounded-full;
  animation: bounce-dot 1.4s infinite ease-in-out both;
}

.loading-dot-1 {
  animation-delay: -0.32s;
}

.loading-dot-2 {
  animation-delay: -0.16s;
}

.loading-dot-3 {
  animation-delay: 0s;
}

@keyframes bounce-dot {
  0%,
  80%,
  100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

/* th button hover — targets shadcn-generated button internals */
:deep(thead th button:hover) {
  background: transparent !important;
}
</style>
